import ExcelJS from 'exceljs';
import {
  FileResult,
  ExcelHeader,
  UnitsReferenceEnum,
  UnitsReference
} from '@/modules/results/models/interwinnerResult';

export default async function exportToExcel(
  results: Record<string, FileResult>,
  radionuclides: Set<string>,
  unitsReference: UnitsReference
): Promise<ExcelJS.Buffer> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('results');
  const unitArr = formatUnitsArray(unitsReference);

  const headers = insertHeaders(worksheet, results, radionuclides, unitsReference, unitArr);
  insertSubHeaders(worksheet, radionuclides, headers, unitArr);
  insertValues(worksheet, results, radionuclides, unitArr);
  styleWorksheet(worksheet, getRnStartCol(headers));

  return await workbook.xlsx.writeBuffer();
}

function insertHeaders(
  worksheet: ExcelJS.Worksheet,
  results: Record<string, FileResult>,
  radionuclides: Set<string>,
  unitsReference: UnitsReference,
  unitArr: string[]
): ExcelHeader[] {
  const headers: ExcelHeader[] = [];
  const maxObs = getMaxObservations(results);
  // headers
  // metadata
  headers.push({ colAttributes: { header: 'Fichier', key: 'file', width: 30 } as ExcelJS.Column, isRn: false });
  headers.push({
    colAttributes: { header: "Date de l'acquisition", key: 'dateOfAcquisition', width: 30 } as ExcelJS.Column,
    isRn: false
  });
  headers.push({
    colAttributes: {
      header: "Durée de l'acquisition (" + unitsReference.metadata[UnitsReferenceEnum.REALTIME] + ')',
      key: 'realTime',
      width: 30
    } as ExcelJS.Column,
    isRn: false
  });
  headers.push({
    colAttributes: {
      header: 'Masse  (' + unitsReference.metadata[UnitsReferenceEnum.MASS] + ')',
      key: 'mass',
      width: 15
    } as ExcelJS.Column,
    isRn: false
  });
  for (let i = 0; i < maxObs; i++) {
    headers.push({
      colAttributes: {
        header: `Observation ${i + 1}`,
        key: `observation${i + 1}`,
        width: 15
      } as ExcelJS.Column,
      isRn: false
    });
  }

  // radionuclides
  radionuclides.forEach((rnName) => {
    for (let i = 0; i < unitArr.length; i++) {
      headers.push({
        colAttributes: {
          header: formatRnName(rnName),
          key: rnName,
          width: 15
        } as ExcelJS.Column,
        isRn: true
      });
    }
  });
  worksheet.columns = headers.map((header) => header.colAttributes);
  return headers;
}

function insertSubHeaders(
  worksheet: ExcelJS.Worksheet,
  radionuclides: Set<string>,
  headers: ExcelHeader[],
  unitArr: string[]
): void {
  // subHeaders
  const subHeaders: string[] = [];
  // empty cells before radionuclides units
  for (let i = 0; i < getRnStartCol(headers) - 1; i++) {
    subHeaders.push('');
  }
  // radionuclides units
  radionuclides.forEach(() => {
    for (const unit of unitArr) {
      subHeaders.push(unit);
    }
  });
  worksheet.addRow(subHeaders);
}

function insertValues(
  worksheet: ExcelJS.Worksheet,
  results: Record<string, FileResult>,
  radionuclides: Set<string>,
  unitArr: string[]
): void {
  const maxObs = getMaxObservations(results);
  // values
  for (const entry of Object.entries(results)) {
    const rowValues = [];
    const fileName = entry[0].split('.')[0];
    const res = entry[1];

    // metadata
    rowValues.push(fileName);
    rowValues.push(res.metadata.dateOfAcquisition);
    rowValues.push(res.metadata.realTime);
    rowValues.push(res.metadata.mass);
    rowValues.push(...res.metadata.observations);
    const emptyCols = maxObs - res.metadata.observations.length;
    if (emptyCols > 0) {
      rowValues.push(...Array<string>(emptyCols).fill(''));
    }
    // radionuclides
    radionuclides.forEach((rnName) => {
      if (res.rows[rnName]) {
        if (res.rows[rnName].activity.length > 0) {
          rowValues.push(res.rows[rnName].activity);
          rowValues.push(res.rows[rnName].uncertaintyValue);
        } else {
          rowValues.push('< ' + res.rows[rnName].detectionLimit);
          rowValues.push('');
        }
        rowValues.push(res.rows[rnName].decisionThreshold);
      } else {
        rowValues.push(...Array<string>(unitArr.length).fill(''));
      }
    });
    worksheet.addRow(rowValues);
  }
}

function styleWorksheet(worksheet: ExcelJS.Worksheet, rnStartCol: number): void {
  const headersRow = 1;
  const subHeadersRow = 2;

  // style
  worksheet.eachRow({ includeEmpty: true }, function (row, rowNumber) {
    row.eachCell(function (cell, colNumber) {
      // common style
      cell.font = {
        name: 'Arial',
        family: 2,
        bold: false,
        size: 10
      };
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center'
      };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };

      // headers style
      if (rowNumber === headersRow) {
        // merges each metadata cells with the cells right bellow
        if (colNumber < rnStartCol) {
          worksheet.mergeCells(rowNumber, colNumber, rowNumber + 1, colNumber);
        }
        // merges radionuclides headers with the same name
        if (colNumber >= rnStartCol && !worksheet.getCell(rowNumber, colNumber).isMerged) {
          let identicalRnCpt = 1;
          const crurrentRn = worksheet.getCell(rowNumber, colNumber).value;
          let nextRn = worksheet.getCell(rowNumber, colNumber + identicalRnCpt).value;
          while (crurrentRn === nextRn) {
            identicalRnCpt++;
            nextRn = worksheet.getCell(rowNumber, colNumber + identicalRnCpt).value;
          }
          if (identicalRnCpt > 1) {
            worksheet.mergeCells(rowNumber, colNumber, rowNumber, colNumber + identicalRnCpt - 1);
          }
        }
        row.height = 30;
        cell.font = {
          bold: true
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'f7dc6f' }
        };
      }

      //subHeaders style
      if (rowNumber === subHeadersRow) {
        row.height = 30;
        cell.font = {
          bold: true
        };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'f7dc6f' }
        };
      }
    });
  });
}

function getRnStartCol(headers: ExcelHeader[]): number {
  return (
    headers.filter((header) => {
      return header.isRn === false;
    }).length + 1
  );
}

function getMaxObservations(results: Record<string, FileResult>): number {
  let max = 0;
  for (const entry of Object.entries(results)) {
    const res = entry[1];
    const length = res.metadata.observations.length;

    if (length > max) {
      max = length;
    }
  }

  return max;
}

function formatUnitsArray(unitsReference: UnitsReference): string[] {
  const unitArr = Object.values(unitsReference.measurements);
  unitArr.pop();
  const activityUnitCleaned = 'Activité (' + unitArr[0].split('(')[1];
  const uncertaintyValueUnitCleaned = 'U (k=2) ' + unitArr[1].split('.')[1];
  const decisionThresholdCleaned = 'SD (' + unitArr[2].split('(')[1];
  unitArr[0] = activityUnitCleaned;
  unitArr[1] = uncertaintyValueUnitCleaned;
  unitArr[2] = decisionThresholdCleaned;
  return unitArr;
}

function formatRnName(rnName: string): string {
  const reg = /[a-zA-Z]{2}/;
  return rnName.replace(reg, rnName[0] + rnName[1].toLocaleLowerCase());
}
